/**
 * Type Guards for User-related types
 *
 * These guards help safely handle fields that can be either
 * string IDs or populated objects from the API.
 */

import type {
  PopulatedOrganisation,
  PopulatedUsergroup,
  PopulatedSensorgroup,
} from '../types';

/**
 * Check if a value is a populated organisation object (not a string ID)
 */
export function isPopulatedOrganisation(
  org: string | PopulatedOrganisation | null | undefined
): org is PopulatedOrganisation {
  return (
    org !== null &&
    org !== undefined &&
    typeof org !== 'string' &&
    '_id' in org &&
    'name' in org
  );
}

/**
 * Check if a value is a populated usergroup object (not a string ID)
 */
export function isPopulatedUsergroup(
  ug: string | PopulatedUsergroup | null | undefined
): ug is PopulatedUsergroup {
  return (
    ug !== null &&
    ug !== undefined &&
    typeof ug !== 'string' &&
    '_id' in ug &&
    'name' in ug
  );
}

/**
 * Check if a value is a populated sensorgroup object (not a string ID)
 */
export function isPopulatedSensorgroup(
  sg: string | PopulatedSensorgroup | null | undefined
): sg is PopulatedSensorgroup {
  return (
    sg !== null &&
    sg !== undefined &&
    typeof sg !== 'string' &&
    '_id' in sg &&
    'name' in sg
  );
}

/**
 * Safely get the ID from an organisation field
 */
export function getOrganisationId(
  org: string | PopulatedOrganisation | null | undefined
): string | null {
  if (!org) return null;
  if (typeof org === 'string') return org;
  return org._id || null;
}

/**
 * Safely get the name from an organisation field
 */
export function getOrganisationName(
  org: string | PopulatedOrganisation | null | undefined
): string | null {
  if (!org) return null;
  if (typeof org === 'string') return org;
  return org.name || null;
}

/**
 * Safely get the ID from a usergroup field
 */
export function getUsergroupId(
  ug: string | PopulatedUsergroup | null | undefined
): string | null {
  if (!ug) return null;
  if (typeof ug === 'string') return ug;
  return ug._id || null;
}

/**
 * Safely get the ID from a sensorgroup field
 */
export function getSensorgroupId(
  sg: string | PopulatedSensorgroup | null | undefined
): string | null {
  if (!sg) return null;
  if (typeof sg === 'string') return sg;
  return sg._id || null;
}
